import React, { useState, useMemo, useCallback, useEffect } from 'react'

import { Button, Icon, FileEmpty } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func, number } from 'prop-types'

import Pagination from 'Pods/Shared/Components/Pagination/Pagination'
import CustomSelect from 'Pods/Shared/CustomSelect/CustomSelect'
import I18n from 'Pods/Shared/I18n'
import Sidebar from 'Pods/Shared/Sidebar/Sidebar'

import { NOOP } from 'Utils/stableDefaults'

import SubscriptionDetailTable from './SubscriptionDetailTable'
import SubscriptionEmptyState from './SubscriptionEmptyState'
import { formatMoneyValue } from '../../../formatter'
import useAvailableMonths from '../../../Hooks/useAvailableMonths'
import useEarningsDetail, { DEFAULT_PER_PAGE } from '../../../Hooks/useEarningsDetail'
import {
  EARNINGS_DETAIL_TYPES,
  PER_PAGE_OPTIONS,
  SUBSCRIPTION_PANEL_CONFIG,
} from '../../../Static/earningsDetailConfig'
import { ALL_MONTHS_OPTION, ALL_MONTHS_VALUE, sortedDescending, ymToOption } from '../../../Static/monthOptions'

const SEARCHABLE_THRESHOLD = 12

function SubscriptionDetailPanel({
  isOpen,
  onClose,
  isParticipating = false,
  onEnableSubscription = NOOP,
  thisMonthAmount = 0,
  downloadCount = 0,
}) {
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE)
  const [monthOption, setMonthOption] = useState(ALL_MONTHS_OPTION)

  const {
    subscriptionSales: availableMonths,
    isLoading: isLoadingMonths,
  } = useAvailableMonths({ enabled: isOpen && isParticipating })

  const monthOptions = useMemo(() => {
    const set = new Set(availableMonths)
    if (monthOption.value !== ALL_MONTHS_VALUE) set.add(monthOption.value)
    return [ALL_MONTHS_OPTION, ...sortedDescending([...set]).map(ymToOption)]
  }, [availableMonths, monthOption.value])

  const selectedMonth = monthOption.value === ALL_MONTHS_VALUE ? null : monthOption.value
  const isSearchable = monthOptions.length > SEARCHABLE_THRESHOLD

  useEffect(() => { setPage(1) }, [selectedMonth])

  const {
    rows, meta, isLoading, error, generateReport, isReportRequired,
  } = useEarningsDetail({
    type: EARNINGS_DETAIL_TYPES.subscriptionEarnings,
    page,
    perPage,
    month: selectedMonth,
    enabled: isOpen && isParticipating,
  })

  const translate = useCallback(
    (key, options = {}) => I18n.t(key, { ns: 'rails', ...options }),
    []
  )

  const handleMonthChange = useCallback((option) => {
    setMonthOption(option || ALL_MONTHS_OPTION)
  }, [])

  const handlePageChange = useCallback((nextPage) => {
    setPage(nextPage)
  }, [])

  const handlePerPageChange = useCallback((nextPerPage) => {
    setPerPage(nextPerPage)
    setPage(1)
  }, [])

  const hasRows = rows.length > 0
  const showEmptyState = !error && (!isParticipating || (!isLoading && !hasRows))
  const showTable = !error && isParticipating && (isLoading || hasRows)
  const showFooter = showTable && hasRows && meta.totalCount > 0

  return (
    <Sidebar
      isOpen={isOpen}
      closeSidebar={onClose}
      title={SUBSCRIPTION_PANEL_CONFIG.title}
      className="subscription-detail-panel"
    >
      <div className="subscription-detail-panel__header">
        <span className="subscription-detail-panel__subtitle cgt-body-base">
          ${formatMoneyValue(thisMonthAmount)} this month &middot;{' '}
          {downloadCount} {SUBSCRIPTION_PANEL_CONFIG.subtitleLabel}
        </span>
        <CustomSelect
          className="subscription-detail-panel__month-select overview-select"
          valueClassName="cgt-body-base-semibold"
          options={monthOptions}
          value={monthOption}
          onChange={handleMonthChange}
          ariaLabel="Filter by month"
          isLoading={isLoadingMonths}
          searchable={isSearchable}
        />
      </div>

      {showTable && (
        <SubscriptionDetailTable rows={rows} isLoading={isLoading} />
      )}

      {showEmptyState && (
        <SubscriptionEmptyState
          onEnable={onEnableSubscription}
          showEnableButton={!isParticipating}
        />
      )}

      {showFooter && (
        <>
          <div className="subscription-detail-panel__report">
            {isReportRequired ? (
              <p className="cgt-body-base">{translate('profile.my_sales.earnings_detail.report_sent_confirmation')}</p>
            ) : (
              <Button
                type="secondary"
                size="small"
                icon={<Icon svgComponent={FileEmpty} />}
                onClick={generateReport}
              >
                {translate('profile.my_sales.earnings_detail.generate_report')}
              </Button>
            )}
          </div>
          <div className="subscription-detail-panel__pagination">
            <Pagination
              currentPage={meta.page}
              totalPages={Math.max(meta.totalPages, 1)}
              totalCount={meta.totalCount}
              perPage={meta.perPage}
              onPageChange={handlePageChange}
              onPerPageChange={handlePerPageChange}
              perPageOptions={PER_PAGE_OPTIONS}
              alwaysShow
            />
          </div>
        </>
      )}
    </Sidebar>
  )
}

SubscriptionDetailPanel.propTypes = {
  isOpen: bool.isRequired,
  onClose: func.isRequired,
  isParticipating: bool,
  onEnableSubscription: func,
  thisMonthAmount: number,
  downloadCount: number,
}

export default SubscriptionDetailPanel
